import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  FilterColumns,
  SelectedLinkClickInsightsUtmFilterColumnsData,
} from 'src/types/analytics-genesis';
import {
  linkClickInsightsUtmAllClicksTableColumnsQuery,
  linkClickInsightsUtmAllNodesTableColumnsQuery,
  linkClickInsightsUtmAllWorkflowsTableColumnsQuery,
  linkClickInsightsUtmUTMAnalysisTableColumnsQuery,
  linkClickInsightsUtmEngagementTableColumnsQuery,
} from 'src/utils/analytics-genesis';
import {fetching, key} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {thunkify as flow} from 'src/utils/thunks';


export const SET_CURRENT_DASHBOARD_ID =
  'analytics-link-click-insights-utm/set-current-dashboard';
export const SET_SELECTED_DATE_RANGE =
  'analytics-link-click-insights-utm/set-selected-date-range';
export const RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_CLICKS_TABLE_COLUMNS =
  'analytics-link-click-insights-utm/receive-link-click-insights-utm-all-clicks-table-columns';
export const RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_WORKFLOWS_TABLE_COLUMNS =
  'analytics-link-click-insights-utm/receive-link-click-insights-utm-all-workflows-table-columns';
export const RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_NODES_TABLE_COLUMNS =
  'analytics-link-click-insights-utm/receive-link-click-insights-utm-all-nodes-table-columns';
export const RECEIVE_LINK_CLICK_INSIGHTS_UTM_UTM_ANALYSIS_TABLE_COLUMNS =
  'analytics-link-click-insights-utm/receive-link-click-insights-utm-utm-analysis-table-columns';
export const RECEIVE_LINK_CLICK_INSIGHTS_UTM_ENGAGEMENT_TABLE_COLUMNS =
  'analytics-link-click-insights-utm/receive-link-click-insights-utm-engagement-table-columns';

type SetCurrentDashboardIdAction = {
  type: typeof SET_CURRENT_DASHBOARD_ID;
  payload: string | null | undefined;
};

export type SetSelectedDateRangeAction = {
  type: typeof SET_SELECTED_DATE_RANGE;
  payload: DateRangeWithTimezone;
};

export type ReceiveLinkClickInsightsUtmAllClicksTableColumnsAction = {
  type: typeof RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_CLICKS_TABLE_COLUMNS;
  payload: SelectedLinkClickInsightsUtmFilterColumnsData;
};

export type ReceiveLinkClickInsightsUtmAllWorkflowsTableColumnsAction = {
  type: typeof RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_WORKFLOWS_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveLinkClickInsightsUtmAllNodesTableColumnsAction = {
  type: typeof RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_NODES_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveLinkClickInsightsUtmUTMAnalysisTableColumnsAction = {
  type: typeof RECEIVE_LINK_CLICK_INSIGHTS_UTM_UTM_ANALYSIS_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type ReceiveLinkClickInsightsUtmEngagementTableColumnsAction = {
  type: typeof RECEIVE_LINK_CLICK_INSIGHTS_UTM_ENGAGEMENT_TABLE_COLUMNS;
  payload: {columns: FilterColumns};
};

export type AnalyticsLinkClickInsightsUtmActions =
  | SetCurrentDashboardIdAction
  | SetSelectedDateRangeAction
  | ReceiveLinkClickInsightsUtmAllClicksTableColumnsAction
  | ReceiveLinkClickInsightsUtmAllWorkflowsTableColumnsAction
  | ReceiveLinkClickInsightsUtmAllNodesTableColumnsAction
  | ReceiveLinkClickInsightsUtmUTMAnalysisTableColumnsAction
  | ReceiveLinkClickInsightsUtmEngagementTableColumnsAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetSelectedDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const receiveLinkClickInsightsUtmAllClicksTableColumns = (
  payload: SelectedLinkClickInsightsUtmFilterColumnsData,
): ReceiveLinkClickInsightsUtmAllClicksTableColumnsAction => ({
  type: RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_CLICKS_TABLE_COLUMNS,
  payload,
});

export const getLinkClickInsightsUtmAllClicksTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getLinkClickInsightsUtmAllClicksTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedLinkClickInsightsUtmFilterColumnsData =
        await dispatch(
          reduxApi.get(
            'analytics/external-link-clicks-workflows/column-selection',
            linkClickInsightsUtmAllClicksTableColumnsQuery,
          ),
        );
      dispatch(receiveLinkClickInsightsUtmAllClicksTableColumns(payload));
    } catch {
      // Error fetching link click insights utm all clicks table columns
    }
  });

export const saveLinkClickInsightsUtmAllClicksTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/external-link-clicks-workflows/column-selection',
        columnData,
        {},
      ),
    ),
);

export const receiveLinkClickInsightsUtmAllWorkflowsTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveLinkClickInsightsUtmAllWorkflowsTableColumnsAction => ({
  type: RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_WORKFLOWS_TABLE_COLUMNS,
  payload,
});

export const receiveLinkClickInsightsUtmAllNodesTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveLinkClickInsightsUtmAllNodesTableColumnsAction => ({
  type: RECEIVE_LINK_CLICK_INSIGHTS_UTM_ALL_NODES_TABLE_COLUMNS,
  payload,
});

export const receiveLinkClickInsightsUtmUTMAnalysisTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveLinkClickInsightsUtmUTMAnalysisTableColumnsAction => ({
  type: RECEIVE_LINK_CLICK_INSIGHTS_UTM_UTM_ANALYSIS_TABLE_COLUMNS,
  payload,
});

export const receiveLinkClickInsightsUtmEngagementTableColumns = (payload: {
  columns: FilterColumns;
}): ReceiveLinkClickInsightsUtmEngagementTableColumnsAction => ({
  type: RECEIVE_LINK_CLICK_INSIGHTS_UTM_ENGAGEMENT_TABLE_COLUMNS,
  payload,
});

export const getLinkClickInsightsUtmAllWorkflowsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getLinkClickInsightsUtmAllWorkflowsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          'analytics/external-link-clicks-workflows/column-selection',
          linkClickInsightsUtmAllWorkflowsTableColumnsQuery,
        ),
      );
      dispatch(receiveLinkClickInsightsUtmAllWorkflowsTableColumns(payload));
    } catch {
      // Error fetching link click insights utm all workflows table columns
    }
  });

export const saveLinkClickInsightsUtmAllWorkflowsTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/external-link-clicks-workflows/column-selection',
        columnData,
        {},
      ),
    ),
);

export const getLinkClickInsightsUtmAllNodesTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getLinkClickInsightsUtmAllNodesTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          'analytics/external-link-clicks-workflows/column-selection',
          linkClickInsightsUtmAllNodesTableColumnsQuery,
        ),
      );
      dispatch(receiveLinkClickInsightsUtmAllNodesTableColumns(payload));
    } catch {
      // Error fetching link click insights utm all workflows table columns
    }
  });

export const saveLinkClickInsightsUtmAllNodesTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/external-link-clicks-workflows/column-selection',
        columnData,
        {},
      ),
    ),
);

export const getLinkClickInsightsUtmUTMAnalysisTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getLinkClickInsightsUtmUTMAnalysisTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          'analytics/external-link-clicks-workflows/column-selection',
          linkClickInsightsUtmUTMAnalysisTableColumnsQuery,
        ),
      );
      dispatch(receiveLinkClickInsightsUtmUTMAnalysisTableColumns(payload));
    } catch {
      // Error fetching link click insights utm utm analysis table columns
    }
  });

export const saveLinkClickInsightsUtmUTMAnalysisTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/external-link-clicks-workflows/column-selection',
        columnData,
        {},
      ),
    ),
);

export const getLinkClickInsightsUtmEngagementTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getLinkClickInsightsUtmEngagementTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: {columns: FilterColumns} = await dispatch(
        reduxApi.get(
          'analytics/external-link-clicks-workflows/column-selection',
          linkClickInsightsUtmEngagementTableColumnsQuery,
        ),
      );
      dispatch(receiveLinkClickInsightsUtmEngagementTableColumns(payload));
    } catch {
      // Error fetching link click insights utm engagement table columns
    }
  });

export const saveLinkClickInsightsUtmEngagementTableFilterColumns: (columnData: {
  columns: FilterColumns;
}) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        'analytics/external-link-clicks-workflows/column-selection',
        columnData,
        {},
      ),
    ),
);
